import { Box, InputLabel, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

type FieldState = { error?: boolean; disabled?: boolean };

export const FrameLabel = styled(InputLabel, {
  shouldForwardProp: prop => prop !== 'error' && prop !== 'disabled',
})<FieldState>(({ theme, error, disabled }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(0, 0.5),
  transform: 'translate(14px, -9px) scale(0.75)',
  color: disabled
    ? theme.palette.text.disabled
    : error
      ? theme.palette.error.main
      : theme.palette.text.primary,
}));

export const FrameSurface = styled(Box, {
  shouldForwardProp: prop => !['error', 'disabled', 'focused'].includes(String(prop)),
})<FieldState & { focused: boolean }>(({ theme, error, disabled, focused }) => {
  const accent = error ? theme.palette.error.main : theme.palette.primary.main;
  const idleBorder = 'rgba(0, 0, 0, 0.23)';

  return {
    borderRadius: theme.shape.borderRadius,
    border: '1px solid',
    borderColor: disabled
      ? theme.palette.text.secondary
      : error
        ? theme.palette.error.main
        : focused
          ? theme.palette.primary.main
          : idleBorder,
    boxShadow: focused ? `0 0 0 1px ${accent}` : 'none',
    transition: theme.transitions.create(['border-color', 'box-shadow'], {
      duration: 200,
      easing: theme.transitions.easing.easeOut,
    }),
    cursor: disabled ? 'not-allowed' : 'text',
    ...(!disabled && {
      '&:hover': {
        borderColor: error
          ? theme.palette.error.main
          : focused
            ? theme.palette.primary.main
            : theme.palette.text.primary,
      },
    }),
    // The editor ships its own bordered box; flatten it so this frame is the only outline.
    '& .tiptap': {
      border: 'none !important',
      borderRadius: '0 !important',
      minHeight: 'auto !important',
      height: 'auto !important',
      padding: '16.5px 14px !important',
    },
    '& .ProseMirror-focused': {
      outline: 'none !important',
    },
    '& > div': {
      minHeight: 'auto !important',
      height: 'auto !important',
      border: 'none !important',
    },
    ...(disabled && {
      opacity: 0.4,
      pointerEvents: 'none',
    }),
  };
});

export const FrameHelperText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'error' && prop !== 'disabled',
})<FieldState>(({ theme, error, disabled }) => ({
  display: 'block',
  marginTop: theme.spacing(0.375),
  marginLeft: theme.spacing(1.75),
  color: disabled
    ? theme.palette.text.disabled
    : error
      ? theme.palette.error.main
      : theme.palette.text.secondary,
}));
